/**
 * Host-aware product site: one SPA build, three public hosts.
 *
 *   dashboard.lunes-crypto.ru → hub (balances + Settings/Sessions + links to paper/live)
 *   paper.lunes-crypto.ru     → paper shell (no Live / Sessions nav clutter)
 *   live.lunes-crypto.ru      → live shell (Live + ops/logs; no Sessions)
 *   localhost / other         → full shell (dev; Sessions in sidebar for testing)
 */

export const SITE_HOSTS = {
  hub: 'dashboard.lunes-crypto.ru',
  paper: 'paper.lunes-crypto.ru',
  live: 'live.lunes-crypto.ru',
}

export const SITE_ORIGINS = {
  hub: `https://${SITE_HOSTS.hub}`,
  paper: `https://${SITE_HOSTS.paper}`,
  live: `https://${SITE_HOSTS.live}`,
  landing: 'https://lunes-crypto.ru',
  /** Independent AI paper bot (Caddy → 127.0.0.1:8810, lunes session SSO). */
  ai: 'https://ai.lunes-crypto.ru',
}

/** @typedef {'hub' | 'paper' | 'live' | 'local'} SiteMode */

/**
 * @param {string} [hostname]
 * @returns {SiteMode}
 */
export function detectSiteMode(hostname) {
  const host = String(hostname || (typeof window !== 'undefined' ? window.location.hostname : '') || '')
    .toLowerCase()
    .split(':')[0]
  if (host === SITE_HOSTS.hub) return 'hub'
  if (host === SITE_HOSTS.paper) return 'paper'
  if (host === SITE_HOSTS.live) return 'live'
  return 'local'
}

/** Current browser host mode (evaluated once; hard refresh on host change is fine). */
export const SITE_MODE = detectSiteMode()

export function isHubSite(mode = SITE_MODE) {
  return mode === 'hub'
}

export function isPaperSite(mode = SITE_MODE) {
  return mode === 'paper'
}

export function isLiveSite(mode = SITE_MODE) {
  return mode === 'live'
}

/** Default authenticated landing path for this host. */
export function defaultPathForSite(mode = SITE_MODE) {
  if (mode === 'hub') return '/hub'
  if (mode === 'live') return '/dashboard/live'
  return '/dashboard/overview'
}

/**
 * Whether a dashboard tab id is allowed on this host.
 * Sessions live on hub Settings only (dashboard.*) — not paper/live sidebars.
 * @param {string} tabId
 * @param {SiteMode} [mode]
 */
export function tabAllowedOnSite(tabId, mode = SITE_MODE) {
  const id = String(tabId || '')
  if (mode === 'hub') return false
  if (mode === 'paper') return id !== 'live' && id !== 'sessions'
  if (mode === 'live') {
    return id === 'live' || id === 'ops' || id === 'logs' || id === 'diagnostics'
  }
  // local: keep Sessions for same-host SPA testing
  return true
}

/**
 * Product trading mode forced by host (null = follow API / local).
 * live.* never shows paper; paper.* never shows live badge.
 * @param {SiteMode} [mode]
 * @returns {'paper'|'live'|null}
 */
export function forcedTradingMode(mode = SITE_MODE) {
  if (mode === 'live') return 'live'
  if (mode === 'paper') return 'paper'
  return null
}

/** systemd units for Ops / Logs on this host. */
export function opsUnitsForSite(mode = SITE_MODE) {
  if (mode === 'live') return ['pmbot-live-15m']
  return ['pmbot-paper', 'pmbot-paper-5m']
}

/** Default ops/journal unit for this host. */
export function defaultOpsUnitForSite(mode = SITE_MODE) {
  return opsUnitsForSite(mode)[0]
}

/**
 * If current path is wrong for this host, return redirect path (same host).
 * @param {string} pathname
 * @param {SiteMode} [mode]
 * @returns {string | null}
 */
export function redirectForSitePath(pathname, mode = SITE_MODE) {
  const p = pathname || '/'
  if (mode === 'hub') {
    if (p === '/login' || p.startsWith('/login/')) return null
    if (p === '/hub' || p === '/hub/') return null
    if (p === '/hub/settings' || p.startsWith('/hub/settings/')) return null
    return '/hub'
  }
  if (mode === 'live') {
    if (p === '/' || p === '/hub' || p === '/hub/' || p === '/dashboard' || p === '/dashboard/') {
      return '/dashboard/live'
    }
    if (p === '/dashboard/overview' || p.startsWith('/dashboard/overview/')) {
      return '/dashboard/live'
    }
    if (p === '/dashboard/brain' || p.startsWith('/dashboard/brain/')) {
      return '/dashboard/live'
    }
    if (p === '/dashboard/trades' || p.startsWith('/dashboard/trades/')) {
      return '/dashboard/live'
    }
    if (p === '/dashboard/collect' || p.startsWith('/dashboard/collect/')) {
      return '/dashboard/live'
    }
    if (p === '/dashboard/sessions' || p.startsWith('/dashboard/sessions/')) {
      return '/dashboard/live'
    }
    if (p === '/dashboard/client' || p.startsWith('/dashboard/client/')) {
      return '/dashboard/live'
    }
    return null
  }
  if (mode === 'paper') {
    if (p === '/hub' || p === '/hub/') return '/dashboard/overview'
    if (p === '/dashboard/live' || p.startsWith('/dashboard/live/')) {
      return '/dashboard/overview'
    }
    if (p === '/dashboard/sessions' || p.startsWith('/dashboard/sessions/')) {
      return '/dashboard/overview'
    }
    return null
  }
  return null
}
